import i18n from 'i18next'
import { omMessage } from '@owma/kit'
import { TYPE, ACTION, sortByReceivedAt } from './defConstants'
import EmailObj, { parseEMLByPostalMime } from './EmailObj'
import MailboxObj from './MailboxObj'
import type { MailboxData } from './MailboxObj'
import dataManager from './DataManager'
import attachmentHelper from 'pages/main/components/Attachment/helper'
import { getCid } from 'utils'
import { convert } from 'html-to-text'

import scheduleApiHelper from 'services/schedule-send'
import { findClosestExpirationKey } from 'utils/ui/SecureSend'

const {
  getScheduledEmails,
  deleteScheduledEmail,
  cancelScheduledEmail,
  getEmailStatusCounts,
  createScheduledEmail,
  downloadEmlFile,
} = scheduleApiHelper

export class ScheduleEmail extends EmailObj {
  detail: any | null
  viewSubject: string
  originalData: any
  reqInfo: any

  constructor(dataMgr: any) {
    super(dataMgr)
    this.detail = null
    this.isLoaded = false

    this.reqInfo = {
      waitingReq: [],
      isLoading: false,
    }
  }

  applyData = (data: any) => {
    this.originalData = data
    this.data = {
      id: data.id,
      subject: data.subject,
      from: [
        {
          name: data.from_name,
          email: data.from_email,
        },
      ],
      to: data.to_list,
      cc: data.cc_list,
      bcc: data.bcc_list,
      preview: data.snippet,
    }
    this.isLoaded = true
    if (data != null) {
      this.viewSubject = data.subject
    }
    return true
  }

  isMovable = () => {
    return false
  }

  getViewSubject = (): string => {
    return this.data.subject
  }

  getViewTime = (): string => {
    let scheduledTimeStr = this.originalData.scheduled_time
    if (scheduledTimeStr.endsWith('Z') === false) {
      scheduledTimeStr += 'Z'
    }
    return scheduledTimeStr
  }

  isFlagEnabled = (): boolean => {
    return false
  }

  hasAttachment = (): boolean => {
    return false
  }

  getPreview = (): string => {
    if (this.data != null && this.data.preview != null && this.data.preview.trim().length > 0) {
      return this.data.preview
    }
    return i18n.t('email.desc.noPreview')
  }

  getDeleteType = (): string => {
    return 'destroy'
  }

  isContained = (mailbox: any): boolean => {
    return mailbox.isContain(this)
  }

  isDraft = (): boolean => {
    return false
  }
  setRead = (value: any, targetMailbox: any): any => {
    console.log('setRead', value, targetMailbox)
    return Promise.resolve(null)
  }
  isRead = (): boolean => {
    return true
  }
  isFlagged = (): boolean => {
    return false
  }
  reqDetail = (reload?: boolean): Promise<any> => {
    console.log('reqDetail', reload)
    if (this.detail != null) {
      return Promise.resolve(this.detail)
    }

    if (this.reqInfo.isLoading === false) {
      this.reqInfo.isLoading = true
      return (async () => {
        try {
          const ret = await downloadEmlFile(this.getId())
          const { body } = ret
          const buffer = await body.arrayBuffer()
          const postalMimeEML = await parseEMLByPostalMime(buffer)
          const bodyPart = await this.makeBodyPartFromEML(postalMimeEML)
          this.detail = {}
          this.detail.headers = (postalMimeEML.headers ?? []).map((h: any) => ({
            name: h.key,
            value: h.value,
          }))
          this.detail.bodyPart = bodyPart
          this.detail.hasAttachment = bodyPart.files != null && bodyPart.files.length > 0

          this.reqInfo.waitingReq.forEach((promise) => {
            promise.resolve(this.detail)
          })
          this.reqInfo.waitingReq = []
          this.reqInfo.isLoading = false

          return this.detail
        } catch (err) {
          let error = null
          if (err instanceof Error) {
            error = err
          } else if (err instanceof Blob && err.type === 'application/json') {
            const json = await JSON.parse(await err.text())
            error = new Error(json.message)
            error.code = json.code
          } else {
            error = new Error('schedule email detail download error.')
          }
          this.reqInfo.waitingReq.forEach((promise) => {
            promise.reject(error)
          })
          this.reqInfo.waitingReq = []
          this.reqInfo.isLoading = false
          throw error
        }
      })()
    } else {
      const promise = new Promise((resolve, reject) => {
        this.reqInfo.waitingReq.push({ resolve, reject })
      })
      return promise
    }
  }
  loadSelf = (): Promise<any> => {
    return Promise.resolve(null)
  }
  generateQHeadStr = (): Promise<any> => {
    return Promise.resolve(null)
  }
  downloadEml = (): Promise<any> => {
    return Promise.resolve(null)
  }
  buildEML = () => {
    return Promise.resolve(null)
  }
  saveEML = () => {
    return Promise.resolve(null)
  }
  getPrimaryMessageId = (): string | null => {
    const headers: Array<{ name: string; value: string }> = this.detail?.headers ?? []
    const references = headers.find((h) => h.name.toLowerCase() === 'references')
    if (references != null) {
      const parts = references.value.trim().split(' ')
      return parts[0] ?? null
    }
    const messageId = headers.find((h) => h.name.toLowerCase() === 'message-id')
    return messageId?.value ?? null
  }

  // OMD-151: 예약메일은 자기발신 inbox/sent 쌍에 속하지 않고 data에 messageId도 없으므로 null 처리.
  isSentMail = (): boolean | null => {
    return null
  }

  getMessageId = (): string | null => {
    return null
  }
}

export class ScheduleMailbox extends MailboxObj {
  _listType: any
  useUnreadCnt: any

  offset: number
  limit: number
  total: number | null

  eTag: string | null

  getScheduledEmails: any
  reqState: number
  searchText: string | null

  maxCheckInterval: number = 30 * 60 * 1000
  minCheckInterval: number = 2 * 1000

  checkTimer: any

  isChanged: boolean = false

  constructor(dataMgr: any, options: any) {
    super(dataMgr)
    this.data = {
      id: options.id,
      role: options.role,
      name: options.name,
      parentId: options.parentId,
      totalEmails: 0,
      unreadEmails: 0,
    }
    this.isRealMailbox = false
    this._listType = TYPE.EMAIL
    this.isEmailType = true

    this.threadList = []
    this.emailList = []
    this.emailListForEmail = []

    this.useUnreadCnt = false
    this.searchText = null

    this.offset = 0
    this.limit = options.limit != null ? options.limit : 30
    this.total = null

    this.eTag = null

    this.sortInfo = undefined
  }

  setLimit = (limit: number) => {
    this.limit = limit
  }

  getSortInfo = () => {
    return {
      type: 'sort_schedule',
      direction: 'sort_asc',
    }
  }

  getReqState = () => {
    return this.reqState
  }

  setReqState = (state: number) => {
    this.reqState = state
  }

  applyData = (data: any) => {
    console.log('applyData', data)
    return false
  }

  getId = () => this.data.id
  getRole = () => this.data.role
  getDisplayName = () => {
    const role = this.getRole()
    /* i18next-extract-disable-next-line */
    return i18n.t(`mailbox.displayName.${role}`)
  }
  getFullName = () => this.getDisplayName()

  getViewCountInfo = (): { count: number | null; type: string } | null => {
    return {
      count: this.data.pending,
      type: 'total',
    }
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  setRole = (role: any) => {}
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  rename = (newName: any) => {}
  getParentMailbox = (): any => null
  isContain = (email: any): boolean => {
    return this.getEmailById(email.getId()) != null
  }
  getEmailDeleteType = (): string => {
    return 'destroy'
  }
  getToolbarMode = (): string => {
    return 'scheduled'
  }

  getEmailById = (id: any): any => {
    const email = this.emailListForEmail.find((item) => {
      return item.getId() === id
    })
    return email
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  changeParent = (newParentId: any) => {}

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  delete = (deleteEmails?: boolean) => {
    // 메일 박스 삭제 불가.
    return null
  }
  // 메일박스 하위 메일박스 삭제. (deleteSelf: 자신 메일박스 삭제 여부)
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  deleteChild = (deleteSelf?: boolean) => {
    // 메일 박스 하위 메일박스 삭제 불가. - 하위 메일박스 불가능.
    return null
  }
  // 예약 메일을 서버에서 제거/취소한 뒤 로컬 목록·리스너를 정리하는 공통 로직.
  // apiCall 은 email 당 서버 호출(DELETE 또는 PUT status=cancelled)을 수행하고 StandardResponse 를 반환한다.
  // 반환: email 별 성공 여부(code === 200) 배열.
  private mutateEmails = async (
    emailList: any,
    apiCall: (email: any) => Promise<any>,
  ): Promise<boolean[]> => {
    const errors: any[] = []
    const resultPromises = emailList.map(async (email: any) => {
      try {
        const result = await apiCall(email)
        if (result.code === 200) {
          const pos = this.emailListForEmail.findIndex((emailItem: any) => {
            return emailItem.getId() === email.getId()
          })
          if (pos > -1) {
            this.emailListForEmail.splice(pos, 1)
          }
          this.dataMgr.removeEmail(email)
          return true
        }
        return false
      } catch (err) {
        // 서버가 422/400 등으로 반환한 메시지(예: "예약발송시각 이후에는 삭제할 수 없습니다.")는
        // 사용자 액션이 가능한 경고이므로 unhandled rejection 으로 흘리지 않고 toast 로 안내한다.
        errors.push(err)
        return false
      }
    })
    const resultList = await Promise.all(resultPromises)

    if (errors.length > 0) {
      const firstError = errors[0]
      const errMessage = firstError?.message
      if (errMessage != null && errMessage.length > 0) {
        omMessage.error(errMessage)
      }
    }

    // 실제 성공한 이메일만 listener 에 통보한다.
    // 부분 실패 시 실패 건이 list 에 섞이면 다운스트림이 잘못된 상태로 인식할 수 있다.
    const changedEmailList = emailList.filter((_: any, i: number) => resultList[i] === true)
    if (changedEmailList.length > 0) {
      const notiData = [
        {
          targetType: TYPE.MAILBOX,
          target: this,
          action: ACTION.DELETE,
          type: TYPE.EMAIL,
          data: { list: changedEmailList },
        },
      ]
      this.threadChangeListener.notify(notiData)
    }

    this.notifyChangeAll()

    await this.updateData()

    return resultList
  }

  // 이메일 완전 삭제 (DELETE). 일반 삭제 경로(getDeleteType()==='destroy')와 공유되므로 취소와 분리한다.
  deleteEmails = async (emailList: any) => {
    return this.mutateEmails(emailList, (email: any) => deleteScheduledEmail(email.getId()))
  }

  // 예약 취소 (PUT status=cancelled). cancelled 상태는 목록 조회 필터(status:'pending')에서 제외되어
  // DELETE 와 동일하게 리스트에서 사라진다.
  cancelEmails = async (emailList: any) => {
    return this.mutateEmails(emailList, (email: any) =>
      cancelScheduledEmail(email.getId(), email.getViewTime()),
    )
  }
  // 하위 폴더 가능 여부
  childEnabled = (): boolean => {
    // 메일 박스 하위 폴더 불가능.
    return false
  }

  getListType = () => this._listType
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  notifyCountChanged = (count: any) => {}
  getResultFilter = () => () => false

  resetData = () => {
    this.resetReqData()
  }

  resetReqData = () => {
    this.emailListForEmail = []
    this.offset = 0
    this.total = null
    this.data.totalEmails = 0
    this.data.unreadEmails = 0
    this.isChanged = false

    this.eTag = null
    this.setReqState(-1)
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  getThreadById = (id: any) => {
    return null
  }

  setEmailSearch = (text: string | null) => {
    this.resetData()

    this.searchText = text
  }

  getAllData = (): MailboxData => {
    return super.getAllData()
  }

  insertEmailDataList = (emailDataList: any, isAllData: boolean = true) => {
    if (this.emailListForEmail == null || isAllData === true) {
      this.emailListForEmail = []
    }

    emailDataList.forEach((item: any) => {
      const email = new ScheduleEmail(this.dataMgr)
      email.applyData(item)
      this.dataMgr.addEmail(email)

      // 중복 제거 필요.
      const existItem = this.emailListForEmail.find((emailItem: any) => {
        return emailItem.getId() === email.getId()
      })

      if (existItem == null) {
        this.emailListForEmail.push(email)
      }
    })

    // sort필요.
    this.emailListForEmail.sort((a: any, b: any) => {
      return -sortByReceivedAt(a, b)
    })
  }

  checkSended = () => {
    if (this.checkTimer != null) {
      clearTimeout(this.checkTimer)
    }

    if (this.emailListForEmail.length === 0) {
      return
    }

    const now = new Date().getTime()
    let idx = 0
    let scheduledTime = 0

    do {
      const email = this.emailListForEmail[idx]
      const scheduledTimeStr = email.getViewTime()
      scheduledTime = new Date(scheduledTimeStr).getTime()
      idx++
    } while (scheduledTime < now && idx < this.emailListForEmail.length)

    const remainTime = scheduledTime - now

    if (remainTime > 0) {
      let checkTime = remainTime

      if (remainTime > this.maxCheckInterval) {
        checkTime = this.maxCheckInterval
      } else if (remainTime < this.minCheckInterval) {
        checkTime = this.minCheckInterval
      }

      const self = this
      this.checkTimer = setTimeout(() => {
        self.reqUpdatedThreadData()
      }, checkTime)
    }
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  reqNextThreadList = async (offset?: number, info?: any, dataInfo?: any, needTotal?: boolean) => {
    const newOffset = offset == null ? this.offset : offset
    if (this.total == null || this.total > newOffset) {
      const params = {
        skip: newOffset,
        limit: this.limit,
        order_dir: 'asc',
        // status: 'pending,failed,cancelled',
        status: 'pending', // @todo - 추후에 failed 상태도 보여지도록 해야 함.
      }
      const result = await getScheduledEmails(params)
      if (result.code === 200) {
        const { data } = result

        this.insertEmailDataList(data)

        this.offset = this.emailListForEmail.length
        this.total = result.total
        this.data.totalEmails = this.total

        this.data.pending = this.total

        this.setReqState(this.offset)

        this.isChanged = false
        this.notifyChangeAll()
      }
    }

    this.checkSended()

    const result = {
      emailIds: this.emailListForEmail.map((email) => email.getId()),
      threadList: [],
      allEmailList: this.emailListForEmail.map((email) => email.data),
      total: this.total,
      hasMore: this.total > this.offset ? true : false,
    }

    return result
  }

  reqUpdatedThreadData = async (_isSave?: any) => {
    await this.updateData()
    if (this.isChanged === true) {
      this.offset = 0
      this.total = null
      this.setReqState(-1)

      await this.reqNextThreadList()
    }

    this.checkSended()
    return this.emailListForEmail
  }

  updateData = async (): Promise<any> => {
    try {
      const result = await getEmailStatusCounts(this.eTag)
      if (this.eTag !== result.eTag) {
        this.eTag = result.eTag
        this.isChanged = true
        const { data } = result.data
        const { pending /* , sent, failed */ } = data

        if (this.data.pending !== pending) {
          this.data.pending = pending
          this.dataMgr.mailbox.changeListener.notify({
            action: ACTION.UPDATE,
            type: TYPE.MAILBOX,
            data: { item: this },
          })
        }
      }
      return { result }
    } catch (err) {
      console.log('updateData error : ', err)
      return { result: null }
    }
  }

  getThreadEmailfilter = () => {
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const threadEmailfilter = (emailObj: any) => {
      return true
    }
    return threadEmailfilter
  }

  isMoveEnabled = () => {
    return false
  }

  isSentMailbox = (): boolean => {
    const role = this.getRole()
    return role === 'schedule'
  }
}

export const sendScheduledEmail = async (apiParams: any) => {
  try {
    const response = await createScheduledEmail(apiParams)
    console.log('sendScheduledEmail', response)

    const curMailbox = dataManager.curMailbox
    const scheduleMailbox = dataManager.getMailboxById('schedule') as ScheduleMailbox
    if (curMailbox != null && curMailbox === scheduleMailbox) {
      scheduleMailbox.insertEmailDataList([response.data], false)

      const emailObj = dataManager.getEmailById(response.data.id)
      if (emailObj != null) {
        const notiData = [
          {
            targetType: TYPE.MAILBOX,
            target: scheduleMailbox,
            action: ACTION.CREATE,
            type: TYPE.EMAIL,
            data: { list: [emailObj] },
          },
        ]
        scheduleMailbox.threadChangeListener.notify(notiData)
      }

      scheduleMailbox.notifyChangeAll()
    }

    await scheduleMailbox.updateData()

    console.log('📤 Schedule Send API Response:', response)

    return response
  } catch (error) {
    console.log('sendScheduledEmail error : ', error)
    throw error
  }
}

export const updateScheduledTime = async (emailObj: ScheduleEmail, scheduledTime: string) => {
  try {
    const response = await scheduleApiHelper.updateScheduledEmail(emailObj.getId(), {
      scheduled_time: scheduledTime,
    })

    if (response.code === 200) {
      emailObj.originalData.scheduled_time = response.data.scheduled_time
      if (response.data.downstream_options != null) {
        emailObj.originalData.downstream_options = response.data.downstream_options
      }
      emailObj.reactMgr.updateUI()
      const scheduleMailbox = dataManager.getMailboxById('schedule') as ScheduleMailbox
      if (scheduleMailbox != null) {
        scheduleMailbox.emailListForEmail.sort((a: any, b: any) => {
          return -sortByReceivedAt(a, b)
        })
      }
      scheduleMailbox.threadChangeListener.notify([
        {
          target: scheduleMailbox,
          targetType: TYPE.MAILBOX,
          action: ACTION.UPDATE,
          type: TYPE.EMAIL,
          data: {
            list: [emailObj],
          },
        },
      ])
      dataManager.emailChangeListener.notify({
        target: dataManager,
        targetType: TYPE.DATAMANAGER,
        action: ACTION.UPDATE,
        type: TYPE.EMAIL,
        data: {
          list: [emailObj],
        },
      })
    }
    return response
  } catch (error) {
    console.log('updateScheduledTime error : ', error)

    const err = error as { message?: string; status?: number; response?: { status?: number } }

    let errorMessage: string
    // 서버가 '예약 시간은 현재+60초 이후여야 함' 을 내려주면 사용자 친화 문구로 치환
    if (err.message === 'Scheduled time must be greater than now + 60 seconds') {
      errorMessage = i18n.t('scheduleSend.error.scheduledTimeTooSoon')
    } else {
      errorMessage = err.message || i18n.t('email.error.sendFailed')
      const status = err.status ?? err.response?.status
      if (status === 400) {
        errorMessage = i18n.t('email.error.invalidData')
      } else if (status === 401) {
        errorMessage = i18n.t('email.error.authFailed')
      } else if (status === 422) {
        errorMessage = i18n.t('email.error.scheduledInvalidFormat')
      } else if (status != null && status >= 500) {
        errorMessage = i18n.t('email.error.serverError')
      }
    }

    return { error: errorMessage }
  }
}

export const moveScheduledEmailToDraft = async (emailObj: ScheduleEmail) => {
  if (emailObj == null) {
    return null
  }

  // draft로 저장.

  try {
    await emailObj.reqDetail()
  } catch (err) {
    console.error('[ScheduleMailbox] moveScheduledEmailToDraft reqDetail failed', err)
    return null
  }

  const { from, to, cc, bcc, subject } = emailObj.data

  const htmlBody = emailObj.detail.bodyPart.getHTMLBody()

  let attachments = []
  if (emailObj.detail.hasAttachment === true) {
    attachments = emailObj.detail.bodyPart.files.map((emlfile: any) => {
      // inline인 경우에만 처리되고 있음.
      // 일반 파일일 떄 처리 필요.
      const { filename, content, mimeType, contentId } = emlfile

      let isInline = emailObj.detail.bodyPart.hasHTMLBody() === true && contentId != null

      let attachment = null
      const file = new File([content], filename, { type: mimeType })
      if (isInline === true) {
        const cid = getCid(contentId)
        attachment = attachmentHelper.createAttachment(file, cid, null, false)
        if (htmlBody.indexOf(`cid:${cid}`) > -1) {
          attachment.isUsed = true
        }
      } else {
        attachment = attachmentHelper.createAttachment(file, null, null, false)
      }

      return attachment
    })

    const promiseList = attachments.map(async (attachment) => {
      return attachment.checkUpload()
    })

    await Promise.all(promiseList)
  }

  const htmlBodyText = convert(htmlBody, {
    selectors: [{ selector: 'img', format: 'skip' }],
  })

  // 원본 예약 메일이 보안/수신확인 메일이었으면 draft 헤더로 보존 — 임시보관함에서 다시 열 때
  // EmailForm.checkSecureSendHeader / checkReadReceiptHeader 가 state 를 복원한다.
  // send_mode 4종 (normal / read_receipt / secure_no_read_receipt / secure) 을 두 boolean 으로 분해:
  //   - isSecureMode: 보안 메일 (만료 키 헤더 주입 대상)
  //   - isReadReceiptMode: 수신확인 메일 (수신확인 헤더 주입 대상)
  const sendMode = emailObj.originalData?.send_mode
  const isSecureMode = sendMode === 'secure' || sendMode === 'secure_no_read_receipt'
  const isReadReceiptMode = sendMode === 'secure' || sendMode === 'read_receipt'

  let expirationKey: string | null = null
  if (isSecureMode) {
    const expiresAt = emailObj.originalData?.downstream_options?.expires_at
    const scheduledTime = emailObj.originalData?.scheduled_time
    if (expiresAt != null && scheduledTime != null) {
      expirationKey = findClosestExpirationKey(scheduledTime, expiresAt)
    }
  }

  const mailData = {
    from,
    to,
    cc,
    bcc,
    subject,
    bodyIsHTML: true,
    htmlBody,
    htmlBodyText,
    textBody: null,
    attachments,
    threadId: null,
    inReplyTo: null,
    references: null,
    replyTo: null,
    identityId: null,
    // 본 시스템의 수신확인은 별도 마이크로서비스 처리 → RFC 3798 헤더(dispositionNotificationTo)
    // 는 건드리지 않고 X-ReadReceipt-Enabled 커스텀 헤더만 사용한다.
    isReadReceiptRequested: false,
    isHighPriority: false,
    ...(expirationKey != null && {
      'header:X-Secure-ExpirationKey:asText': expirationKey,
    }),
    ...(isReadReceiptMode && {
      'header:X-ReadReceipt-Enabled:asText': 'true',
    }),
  }

  const ret = await dataManager.createEmail(mailData, null, false)
  const { createdEmail } = ret

  if (createdEmail.notCreated != null) {
    const key0 = Object.keys(createdEmail.notCreated)[0]
    const val = createdEmail.notCreated[key0] // notUpdated는 map & property.
    if (val != null) {
      let error = null
      if (val.type === 'invalidProperties') {
        error = new Error(`invalidProperties - [ ${val.properties.join(', ')} ]`)
      } else if (val.type === 'invalidRecipients') {
        const { invalidRecipients } = val
        error = new Error(`invalidRecipients - ${invalidRecipients.join(', ')}`)
      } else {
        error = new Error(`email create error - ${val.type}`)
      }
      //에러 발생
      throw error
    }
  }

  // draft 생성이 성공했으면 원래 예약 메일을 취소(PUT status=cancelled)한다.
  const scheduleMailbox = dataManager.getMailboxById('schedule') as ScheduleMailbox | null
  if (scheduleMailbox != null) {
    const [cancelled] = await scheduleMailbox.cancelEmails([emailObj])
    if (cancelled !== true) {
      // 취소 실패(예: 이미 발송되어 422): draft 는 이미 생성됐으므로 롤백해
      // "예약 메일은 남고 draft 만 중복 생성" 되는 반쪽 상태를 방지한다. (실패 안내는 cancelEmails 가 처리)
      await rollbackCreatedDraft(createdEmail)
      return null
    }
  }

  return ret
}

// createEmail 로 생성된 draft 를 삭제(롤백)한다. 취소 실패 시 중복 draft 정리에 사용.
const rollbackCreatedDraft = async (createdEmail: any) => {
  const createdEntry = createdEmail?.created != null ? Object.values(createdEmail.created)[0] : null
  const createdId = (createdEntry as { id?: string } | null)?.id
  if (createdId == null) {
    console.warn('[ScheduleMailbox] rollbackCreatedDraft: 생성된 draft id 를 찾지 못함')
    return
  }

  const draftObj = dataManager.getEmailById(createdId)
  const draftsBox = dataManager.getMailboxForRole('drafts')
  if (draftObj == null || draftsBox == null) {
    console.warn('[ScheduleMailbox] rollbackCreatedDraft: draft 객체/메일박스 없음', createdId)
    return
  }

  try {
    await draftsBox.deleteEmails([draftObj])
  } catch (err) {
    console.error('[ScheduleMailbox] rollbackCreatedDraft failed', err)
  }
}

const scheduleMap = {
  email: ScheduleEmail,
  mailbox: ScheduleMailbox,
  sendEmail: sendScheduledEmail,
}

export default scheduleMap
